import { CitiesSelect } from 'components/redesign/CititesSelect';
import { FieldCheckbox } from 'components/redesign/FieldCheckbox';
import { SpecSelect } from 'components/redesign/SpecSelect';
import { EExecutorSpecialization } from 'enums/executorSpecialization';
import { ModerationRequestStatusId } from 'enums/moderationRequestStatus';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import { moderationRequestActions } from 'reduxStore/reducers/ModerationRequestSlice';
import { ProfileSpecialization } from 'reduxStore/reducers/ProfileSlice/types';
import { downloadStaticFile, handleFileDownload } from 'utils/helpers';
import { StaticFiles } from 'utils/settings';
import { isFieldDisabled } from '../../consts';
import { mapExecutorChecklistField } from '../finalStep/consts';
import { specsData } from './consts';
import { Step4Props } from './types';
import { MultiValue } from 'react-select';
import { OptionType } from 'ui/redesign/Select/types';
import { ExtendedSuggestion, SettlementShort } from 'types';

export const Step4Fields: FC<Step4Props> = ({
  control,
  watch,
  errors,
  setValue,
  isFormDisabled,
  parentId,
  clearErrors,
  executorType,
  status = 0,
  id = 0,
  getValues,
  moderationRequestData,
  checkFieldInChecklist,
  isUserAgentFromInvitation,
}) => {
  const isSecondaryModerationForAgent = Boolean(
    id &&
      status !== ModerationRequestStatusId.Draft &&
      status !== ModerationRequestStatusId.ElaborationRequested &&
      [ModerationRequestStatusId.TrainingCompleted, ModerationRequestStatusId.Imported].includes(
        status
      ) &&
      getValues('executorSpecializations')?.includes(EExecutorSpecialization.Agent)
  );
  const dispatch = useAppDispatch();

  const changeExecutorSpecializationsHandle = useCallback(
    (value: EExecutorSpecialization[] | null) => {
      if (!value || !value.length || value?.includes(EExecutorSpecialization.Agent)) {
        setValue('acceptsOffer', false);
        dispatch(
          moderationRequestActions.setEducations({
            educations: [],
            workExperiences: [],
          })
        );
        dispatch(
          moderationRequestActions.setCertificates({
            certificateElectricalSafety: [],
            certificateWorkHeight: [],
          })
        );
        setValue('primaryEducation', []);
        setValue('secondaryEducation', []);
        setValue('workExperiences', []);
        setValue('certificateElectricalSafety', []);
        setValue('certificateWorkHeight', []);
      }
    },
    []
  );
  const downloadHandler = () => {
    if (!getValues('executorSpecializations')?.length) {
      toast.error('Выберите область специализации');
      return;
    }
    getValues('executorSpecializations').includes(ProfileSpecialization.Agent)
      ? handleFileDownload(downloadStaticFile(StaticFiles.Agent_Contract_Offer))
      : handleFileDownload(downloadStaticFile(StaticFiles.SelfEmployed_Contract_Offer));
  };

  const changeCityHandler = (
    value:
      | MultiValue<OptionType<SettlementShort | ExtendedSuggestion>>
      | OptionType<SettlementShort>
      | null
  ) => {
    const actualValues = getValues();

    if (!actualValues.primaryCity) {
      return;
    }

    if (Array.isArray(value)) {
      if (
        actualValues?.serviceCities &&
        actualValues?.serviceCities?.some(
          (item: any) =>
            actualValues?.serviceCities &&
            item.value.fiasId === actualValues?.primaryCity?.value?.fiasId
        )
      ) {
        setValue('primaryCity', null);
      }
    } else {
      const filteredAdditionalSettlements = actualValues.serviceCities.filter(
        (item: any) =>
          actualValues?.primaryCity && item.value.fiasId !== actualValues?.primaryCity.value.fiasId
      );
      setValue('serviceCities', filteredAdditionalSettlements);
    }
  };

  const executorSpecializations = watch('executorSpecializations');

  return (
    <div className="flex flex-col gap-8 sm:grid sm:grid-cols-[minmax(0,600px)_minmax(0,600px)]">
      {parentId === 0 && (
        <>
          <CitiesSelect
            isMulti={false}
            control={control}
            label="Основной город"
            name="primaryCity"
            onChange={changeCityHandler}
            isFormDisabled={
              moderationRequestData?.status === ModerationRequestStatusId.ElaborationRequested
                ? (checkFieldInChecklist &&
                    checkFieldInChecklist(
                      moderationRequestData,
                      mapExecutorChecklistField.ServiceCities
                    )) ||
                  false
                : isFormDisabled
            }
            isClearable
            errorMessage={errors?.primaryCity?.message}
            clearErrors={clearErrors}
          />
          <CitiesSelect
            isMulti={true}
            control={control}
            label="Дополнительные города"
            name="serviceCities"
            onChange={changeCityHandler}
            isFormDisabled={
              moderationRequestData?.status === ModerationRequestStatusId.ElaborationRequested
                ? (checkFieldInChecklist &&
                    checkFieldInChecklist(
                      moderationRequestData,
                      mapExecutorChecklistField.ServiceCities
                    )) ||
                  false
                : isFormDisabled
            }
            isClearable
            clearErrors={clearErrors}
          />
        </>
      )}
      {parentId === 0 && (
        <SpecSelect
          name="executorSpecializations"
          control={control}
          label="Область специализации"
          options={
            isUserAgentFromInvitation
              ? specsData
              : specsData.filter((s) => s.value !== EExecutorSpecialization.Agent)
          }
          watch={watch}
          disabled={
            isFormDisabled || isSecondaryModerationForAgent || Boolean(isUserAgentFromInvitation)
          }
          errorMessage={errors?.executorSpecializations?.message}
          clearErrors={clearErrors}
          executorType={executorType}
          onChange={changeExecutorSpecializationsHandle}
        />
      )}
      <div>
        {parentId > 0 && (
          <SpecSelect
            name="executorSpecializations"
            control={control}
            label="Область специализации"
            options={specsData}
            watch={watch}
            disabled={
              isFormDisabled ||
              getValues('executorSpecializations')?.includes(EExecutorSpecialization.Agent)
            }
            errorMessage={errors?.executorSpecializations?.message}
            clearErrors={clearErrors}
            executorType={executorType}
          />
        )}
        {executorSpecializations?.length > 0 &&
          !executorSpecializations.includes(EExecutorSpecialization.Agent) && (
            <>
              <div className="mt-6 mb-5 flex">
                <FieldCheckbox
                  name="acceptsOffer"
                  control={control}
                  errorMessage={(errors as any)?.acceptsOffer?.message}
                  title="Я принимаю оферту"
                  disabled={isFieldDisabled(
                    mapExecutorChecklistField.Offer,
                    isFormDisabled,
                    moderationRequestData,
                    checkFieldInChecklist
                  )}
                  clearErrors={clearErrors}
                />
              </div>
              <p>
                <span
                  className="footnote-medium sm:callout-normal cursor-pointer !text-primary-60"
                  onClick={downloadHandler}
                >
                  Ссылка на скачивание оферты
                </span>
              </p>
            </>
          )}
      </div>
    </div>
  );
};
